import { type MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiIcon,
  MiMenu,
  MiPopper,
  MiTemplateBadge,
} from "@miplatform/components";
import {
  ATTEMPT_STATUSES,
  BUTTON_COLORS,
  BUTTON_SIZES,
  BUTTON_VARIANTS,
  DEFAULT_COMMUNITY_COLORS,
  MIN_WORK_ORDER_EXPECTED_DURATION_MINUTES,
  ONE_HOUR,
  POPPER_DELAY,
  WORK_ORDER_COLORS_MAP,
  WORK_ORDER_STATUSES_LABELS,
  WORK_ORDER_SUBSTATUSES_LABELS,
} from "@miplatform/constants";
import {
  IAttemptListItemResponseForWorkOrder,
  WorkOrderStatus,
  WorkOrderSubStatus,
} from "@miplatform/types";
import {
  convertTimeToShortHand,
  getWorkOrderCardBorderColor,
  getWorkOrderDueDateColor,
  getWorkOrderStatus,
  hoursToMinutes,
  isToday,
} from "@miplatform/utils";
import {
  mdiAlarmLightOutline,
  mdiCalendarAccountOutline,
  mdiCalendarImportOutline,
  mdiLockOutline,
  mdiMinus,
} from "@mdi/js";
import {
  ButtonProps,
  Checkbox,
  Divider,
  Typography,
  useTheme,
} from "@mui/material";
import { Box, keyframes } from "@mui/system";
import { MiWorkOrderTooltip } from "./MiWorkOrderTooltip";

export const MiWorkOrderCard = ({
  attempt,
  minimized = false,
  className,
  loading = false,
  isHighlighted = false,
  onEventRemove,
  onCardClick,
  isRemovable = false,
  menuItems = [],
  isBulkAssignmentMode = false,
  onCheckboxChange,
  checked = false,
  isLocked = false,
}: MiWorkOrderCardProps) => {
  const theme = useTheme();
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const timerIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    home,
    assignment,
    duration,
    exactExtendedTime = 0,
    expectedDuration,
    assigneeExpectedDuration,
  } = attempt ?? {};

  const { workOrder } = assignment ?? {};
  const { workOrderTemplateColor, abbreviation, workOrderTemplateName } =
    workOrder ?? {};
  const { address, builderShortName, communityName } = home ?? {};
  const { addressLine1, city } = address ?? {};
  const { hardDueDate } = workOrder ?? {};
  const isCompletedAttempt = attempt?.attemptStatus === ATTEMPT_STATUSES.CLOSED;
  const displayExpectedDuration = assigneeExpectedDuration ?? expectedDuration;
  const { isDueToday, status } = useMemo(() => {
    const status = getWorkOrderStatus(attempt);
    const isDueToday = hardDueDate
      ? isToday(hardDueDate) &&
        !isCompletedAttempt &&
        workOrder?.subStatus !== WorkOrderSubStatus.Late
      : false;

    return { isDueToday, status };
  }, [attempt]);

  const {
    cardBackgroundColor = theme.palette.common.white,
    borderStyle,
    textColor,
    borderColor,
  } = (
    WORK_ORDER_COLORS_MAP as Record<
      WorkOrderStatus,
      {
        cardBackgroundColor?: string;
        textColor?: string;
        borderStyle?: string;
        borderColor?: string;
      }
    >
  )[status] || {};

  const headerColor = useMemo(() => {
    if (
      displayExpectedDuration &&
      displayExpectedDuration <= MIN_WORK_ORDER_EXPECTED_DURATION_MINUTES
    ) {
      return cardBackgroundColor;
    }

    const { communityName = "" } = home || {};
    const colors = DEFAULT_COMMUNITY_COLORS;

    if (communityName) {
      const colorIndex =
        communityName.split("").reduce((a, i) => a + i.charCodeAt(0), 0) %
        colors.length;
      return colors[colorIndex];
    }

    return theme.palette.bluegrey.light;
  }, [home?.communityName, cardBackgroundColor, displayExpectedDuration]);

  const displayDuration = `${Math.ceil(
    hoursToMinutes(
      duration
        ? (duration || 0) + exactExtendedTime
        : displayExpectedDuration / ONE_HOUR
    ) || 0
  )}m`;

  const dueDateColor = getWorkOrderDueDateColor(attempt, theme);

  const { classes, cx } = useStyles({
    borderColor:
      (!isCompletedAttempt && getWorkOrderCardBorderColor(attempt, theme)) ||
      borderColor,
    borderStyle,
    isHighlighted,
    headerColor: minimized ? "transparent" : headerColor,
    backgroundColor: cardBackgroundColor,
    minimized,
    loading,
  });

  const color = textColor ?? theme.palette.text.primary;

  const openPopper = (e: MouseEvent<HTMLDivElement>) => {
    const { currentTarget } = e;
    clearPopperTimer();

    timerIdRef.current = setTimeout(() => {
      timerIdRef.current = null;
      setAnchorEl(currentTarget);
    }, POPPER_DELAY);
  };

  const closePopper = () => {
    clearPopperTimer();
    setAnchorEl(null);
  };

  const handleContextMenuOpen = (e: React.MouseEvent<HTMLDivElement>) => {
    closePopper();
    e.preventDefault();

    if (isContextMenuOpen) {
      setIsContextMenuOpen(false);
      setContextMenuPosition(null);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const left = Math.min(Math.max(e.clientX + 2, rect.left), rect.right);
    const top = Math.min(Math.max(e.clientY - 6, rect.top), rect.bottom);

    setContextMenuPosition({ left, top });
    setIsContextMenuOpen(true);
  };

  const handleContextMenuClose = () => {
    setIsContextMenuOpen(false);
    setContextMenuPosition(null);
    closePopper();
  };

  const badgeText = abbreviation ?? workOrderTemplateName?.slice(0, 3) ?? null;

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (isBulkAssignmentMode) {
      e.stopPropagation();
      onCheckboxChange?.(attempt, !checked);
      return;
    }
    onCardClick?.(attempt);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onCheckboxChange?.(attempt, e.target.checked);
  };

  const clearPopperTimer = () => {
    if (timerIdRef.current) {
      clearTimeout(timerIdRef.current);
      timerIdRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearPopperTimer();
    };
  }, []);

  return (
    <Box
      className={cx(classes.card, className)}
      onContextMenu={handleContextMenuOpen}
      onMouseEnter={openPopper}
      onMouseLeave={closePopper}
      onClick={handleClick}
    >
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          {isBulkAssignmentMode && (
            <Checkbox
              sx={{
                "& .MuiSvgIcon-root": { fontSize: 14 },
                padding: 0,
              }}
              size="small"
              color="primary"
              checked={checked}
              onChange={handleCheckboxChange}
              onClick={(ev) => ev.stopPropagation()}
            />
          )}
          {badgeText && (
            <MiTemplateBadge
              title={badgeText}
              backgroundColor={workOrderTemplateColor}
            />
          )}
          <Typography fontSize={9} noWrap>
            {communityName}
          </Typography>
        </Box>
        <Box className={classes.headerRight}>
          <Typography
            fontSize={9}
            fontWeight={theme.fontWeight.semiBold}
            color={theme.palette.grey.medium}
            fontStyle="italic"
            pr={0.25}
            noWrap
          >
            {builderShortName}
          </Typography>
          {isRemovable && (
            <MiButton
              aria-label="Remove"
              size={BUTTON_SIZES.SMALL as ButtonProps["size"]}
              variant={BUTTON_VARIANTS.CONTAINED}
              color={BUTTON_COLORS.ERROR}
              className={classes.removeButton}
              startIcon={{
                path: mdiMinus,
                iconSize: 0.45,
                iconColor: theme.palette.common.white,
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onEventRemove?.();
              }}
            />
          )}
          {isLocked && <MiIcon path={mdiLockOutline} size={0.5} />}
          {isDueToday && (
            <MiIcon
              path={mdiAlarmLightOutline}
              color={theme.palette.warning.main}
              size={0.5}
            />
          )}
        </Box>
      </Box>
      {!minimized && (
        <Box className={classes.body}>
          <Box className={classes.row}>
            <Typography
              fontSize={9}
              color={color}
              textOverflow="ellipsis"
              fontWeight={theme.fontWeight.bold}
              noWrap
            >
              {addressLine1}
            </Typography>
            <Typography
              fontSize={9}
              color={color ?? theme.palette.grey.medium}
              fontStyle="italic"
              pr={0.25}
            >
              {city}
            </Typography>
          </Box>
          <Box className={classes.row}>
            <Box className={classes.row} alignItems="center" flex={1}>
              <Typography
                fontSize={9}
                noWrap
                overflow="hidden"
                textOverflow="ellipsis"
                maxWidth={workOrder?.unableToBeAutoAssign ? 40 : 61}
              >
                {`${WORK_ORDER_STATUSES_LABELS[status]} ${
                  !isCompletedAttempt && workOrder?.subStatus
                    ? ` / ${WORK_ORDER_SUBSTATUSES_LABELS[workOrder.subStatus]}`
                    : ""
                }
                `}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" justifyContent="flex-end">
              <Typography fontSize={9} color={dueDateColor}>
                {hardDueDate
                  ? convertTimeToShortHand(hardDueDate, true)
                  : "-- @ --"}
              </Typography>
            </Box>
            <>
              <Divider flexItem orientation="vertical" />
              <Typography fontSize={9} fontWeight={theme.fontWeight.bold}>
                {displayDuration}
              </Typography>
              {attempt?.forcedNextOn && (
                <MiIcon
                  path={mdiCalendarImportOutline}
                  size={0.5}
                  color={theme.palette.primary.main}
                />
              )}
              {workOrder?.unableToBeAutoAssign && (
                <MiIcon
                  path={mdiCalendarAccountOutline}
                  color={theme.palette.warning.main}
                  size={0.5}
                />
              )}
            </>
          </Box>
        </Box>
      )}
      {menuItems.length > 0 && (
        <MiMenu
          condensed
          data={menuItems}
          menuStyles={{ zIndex: theme.zIndex.tooltip }}
          optionalElement={<span />}
          closeContextMenu={handleContextMenuClose}
          open={isContextMenuOpen}
          anchorPosition={contextMenuPosition}
        />
      )}
      <MiPopper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        arrowColor={workOrderTemplateColor}
        showArrow
      >
        <MiWorkOrderTooltip attempt={attempt} />
      </MiPopper>
    </Box>
  );
};

export interface MiWorkOrderCardProps {
  attempt: IAttemptListItemResponseForWorkOrder;
  minimized?: boolean;
  className?: string;
  loading?: boolean;
  isHighlighted?: boolean;
  isLocked?: boolean;
  menuItems?: {
    label: string;
    icon: string;
    color: string;
    action: () => void;
    hide?: boolean;
  }[];
  onCardClick?: (attempt: IAttemptListItemResponseForWorkOrder) => void;
  onEventRemove?: () => void;
  isRemovable?: boolean;
  isBulkAssignmentMode?: boolean;
  onCheckboxChange?: (
    attempt: IAttemptListItemResponseForWorkOrder,
    isChecked: boolean
  ) => void;
  checked?: boolean;
}

const useStyles = makeStyles<IWorkOrderCardStyleProps>()(
  (
    theme,
    {
      borderColor,
      headerColor,
      backgroundColor,
      isHighlighted,
      borderStyle = "solid",
      loading,
    }
  ) => {
    const blinkAnimation = keyframes`
            0% {
              box-shadow: 0 0 0 0 ${theme.palette.orange.mediumLight};
            }
            50% {
              box-shadow: 0 0 5px 4px ${theme.palette.orange.mediumLight};
            }
            100% {
              box-shadow: 0 0 0 0 ${theme.palette.orange.mediumLight};
            }
          `;

    return {
      card: {
        border: `1.5px ${borderStyle} ${borderColor}`,
        borderRadius: 4,
        backgroundColor,
        overflow: "hidden",
        height: "100%",
        width: 160,
        "&:hover": {
          cursor: "grab",
        },
        "&:active": { cursor: "grabbing", opacity: 0.5 },
        opacity: loading ? 0.5 : 1,
        pointerEvents: loading ? "none" : "auto",
        ...(isHighlighted && {
          border: `1px solid ${theme.palette.orange.medium}`,
          boxShadow: `0px 0px 3px 1px ${theme.palette.orange.mediumLight}`,
          animation: `${blinkAnimation} 800ms infinite`,
        }),
      },
      header: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(0.5),
        backgroundColor: headerColor,
        height: theme.spacing(4),
      },
      body: {
        padding: theme.spacing(0.5),
      },
      row: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        gap: theme.spacing(1),
        minWidth: 0,
      },
      headerLeft: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing(1),
        minWidth: 0,
        flex: 1,
      },
      headerRight: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: theme.spacing(0.25),
        flexShrink: 0,
        minWidth: 0,
      },
      removeButton: {
        "&&": {
          flexShrink: 0,
          width: 14,
          height: 14,
          minWidth: 14,
          minHeight: 14,
          padding: 0,
          borderRadius: "50%",
          "&:hover": {
            backgroundColor: theme.palette.error.dark,
          },
          "& .MuiButton-startIcon": {
            margin: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
        },
      },
    };
  }
);

export interface IWorkOrderCardStyleProps {
  borderColor?: string;
  headerColor?: string;
  backgroundColor?: string;
  borderStyle?: string;
  minimized?: boolean;
  isHighlighted?: boolean;
  loading?: boolean;
}
